import { Group as LayerGroup, Image as ImageLayer } from 'ol/layer';
import { fromLonLat } from 'ol/proj';
import { ImageStatic } from 'ol/source';

import ExtentUtils from '../utils/ExtentUtils';

/**
 * Photo(사진) 오버레이 관리 Feature
 */
class PhotoFeature {
  constructor(createOverlay, map) {
    this.createOverlay = createOverlay;
    this.map = map;
    this.currentZIndex = 100;
    this.photos = {};
    // {
    //   [robotId]: {
    //     marker: { [imageId]: overlay },
    //     footprint: {
    //       layerGroup,
    //       data: { [imageId]: { url, position, aglAltitude, layer } },
    //     },
    //     type,
    //   },
    //   ...
    // }
  }

  /**
   * 사진 추가
   * @param {string} robotId - 로봇 ID
   * @param {string} imageId - 이미지 ID
   * @param {HTMLElement} element - 마커 DOM 엘리먼트
   * @param {Object} position - { lat, lng } 좌표
   * @param {string} url - 이미지 URL
   * @param {number} aglAltitude - 지상 고도
   * @param {number} focalLength - 초점 거리
   * @param {Object} sensorSize - 센서 크기 { width, height }
   * @param {boolean} visible - 표시 여부
   * @param {string} type - 'marker' 또는 'footprint'
   */
  add(robotId, imageId, element, position, url, aglAltitude, focalLength, sensorSize, visible, type) {
    // 로봇별 photo 데이터 초기화
    if (!this.photos[robotId]) {
      const layerGroup = new LayerGroup({ layers: [] });
      layerGroup.setVisible(type === 'footprint' && visible);
      this.map.addLayer(layerGroup);

      this.photos[robotId] = {
        marker: {},
        footprint: {
          layerGroup,
          data: {},
        },
        type,
      };
    }

    const photos = this.photos[robotId];

    // 기존 imageId가 있으면 제거
    if (photos.marker[imageId]) {
      this.map.removeOverlay(photos.marker[imageId]);
      delete photos.marker[imageId];
    }
    if (photos.footprint.data[imageId]) {
      photos.footprint.layerGroup.getLayers().remove(photos.footprint.data[imageId].layer);
      delete photos.footprint.data[imageId];
    }

    // 최신 사진이 위에 표시되도록 z-index 증가
    this.currentZIndex++;

    // Add marker-type photo
    const overlay = this.createOverlay({
      element,
      position: fromLonLat([position.lng, position.lat]),
      positioning: 'bottom-center',
    });

    // 마커의 z-index 설정 (최신 항목 우선 표시)
    overlay.getElement().parentElement.style.zIndex = this.currentZIndex;
    overlay.getElement().parentElement.style.display = type === 'marker' && visible ? 'block' : 'none';
    this.map.addOverlay(overlay);

    photos.marker[imageId] = overlay;

    //! footprint 임시 비활성화
    // Add footprint-type photo
    // const imageExtent = ExtentUtils.calculatePhotoExtent(position, aglAltitude, focalLength, sensorSize);
    // const source = new ImageStatic({ url, imageExtent });
    // const layer = new ImageLayer({ source });
    // footprint 레이어의 z-index 설정 (최신 항목 우선 표시)
    // layer.setZIndex(this.currentZIndex);

    // photos.footprint.layerGroup.getLayers().push(layer);
    // photos.footprint.data[imageId] = { url, position, aglAltitude, layer };
  }

  /**
   * 사진 표시/숨김
   * @param {string} robotId - 로봇 ID
   * @param {boolean} visible - 표시 여부
   */
  setVisible(robotId, visible) {
    if (!this.photos[robotId]) return;

    const photos = this.photos[robotId];

    if (photos.type === 'marker') {
      Object.values(photos.marker).forEach((overlay) => {
        overlay.getElement().style.display = visible ? 'block' : 'none';
      });
    } else {
      photos.footprint.layerGroup.setVisible(visible);
    }
  }

  /**
   * 사진 타입 변경
   * @param {string} robotId - 로봇 ID
   * @param {string} type - 'marker' 또는 'footprint'
   */
  setType(robotId, type) {
    if (!this.photos[robotId]) return;

    const photos = this.photos[robotId];

    Object.values(photos.marker).forEach((overlay) => {
      overlay.getElement().style.display = type === 'marker' ? 'block' : 'none';
    });
    photos.footprint.layerGroup.setVisible(type === 'footprint');
    photos.type = type;
  }

  /**
   * 사진 크기 조정 (focal length, sensor size 변경 시)
   * @param {string} robotId - 로봇 ID
   * @param {number} focalLength - 초점 거리
   * @param {Object} sensorSize - 센서 크기 { width, height }
   */
  resize(robotId, focalLength, sensorSize) {
    if (!this.photos[robotId]) return;

    const footprints = this.photos[robotId].footprint.data;

    Object.values(footprints).forEach(({ url, position, aglAltitude, layer }) => {
      const imageExtent = ExtentUtils.calculatePhotoExtent(position, aglAltitude, focalLength, sensorSize);
      const source = new ImageStatic({ url, imageExtent });
      layer.setSource(source);
    });
  }

  /**
   * 사진 제거
   * @param {string} robotId - 로봇 ID
   */
  remove(robotId) {
    if (!this.photos[robotId]) return;

    Object.values(this.photos[robotId].marker).forEach((overlay) => this.map.removeOverlay(overlay));
    this.map.removeLayer(this.photos[robotId].footprint.layerGroup);

    delete this.photos[robotId];
  }

  /**
   * Photo 데이터 가져오기
   * @param {string} robotId - 로봇 ID
   * @returns {Object|undefined} Photo 데이터
   */
  get(robotId) {
    return this.photos[robotId];
  }
}

export default PhotoFeature;
